import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from '@tanstack/react-query';
import { Toaster, toast } from 'sonner';
import { ApiError, toastFromApiError, type ToastFn } from '@shared/api-errors';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import { ErrorBoundary } from './components/ErrorBoundary';
import Login from './pages/Login';
import './index.css';

// Lazy-loaded pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Tenants = lazy(() => import('./pages/Tenants'));
const NewTenant = lazy(() => import('./pages/NewTenant'));
const TenantDetail = lazy(() => import('./pages/TenantDetail'));
const Packages = lazy(() => import('./pages/Packages'));
const NewPackage = lazy(() => import('./pages/NewPackage'));
const PackageDetail = lazy(() => import('./pages/PackageDetail'));
const Modules = lazy(() => import('./pages/Modules'));
const Subscriptions = lazy(() => import('./pages/Subscriptions'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Reports = lazy(() => import('./pages/Reports'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const Settings = lazy(() => import('./pages/Settings'));

const PageFallback = () => (
  <div className="min-h-screen bg-slate-950 flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
  </div>
);

// sonner's `toast.error(title, { description })` signature needs bridging to
// api-errors' ToastFn. Surface the resolved TR message as the visible line.
const toastAdapter: ToastFn = (p) => {
  toast.error(p.title, { description: p.description });
};

// queryClient is module-scoped. <Toaster /> must be rendered in the component
// tree (mounted below in App) so cache onError toasts surface.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, err) => {
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false;
        return failureCount < 1;
      },
      staleTime: 5 * 60 * 1000,
    },
    mutations: {
      retry: (failureCount, err) => {
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false;
        return failureCount < 1;
      },
    },
  },
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => {
      if (mutation.options.onError) return;
      if (mutation.options.meta?.suppressErrorToast) return;
      toastFromApiError(err, toastAdapter);
    },
  }),
  queryCache: new QueryCache({
    onError: (err, query) => {
      if (query.meta?.suppressErrorToast) return;
      toastFromApiError(err, toastAdapter);
    },
  }),
});

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" /> : <Login />}
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><Dashboard /></Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/tenants"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><Tenants /></Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/tenants/new"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><NewTenant /></Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/tenants/:id"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><TenantDetail /></Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/packages"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><Packages /></Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/packages/new"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><NewPackage /></Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/packages/:id"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><PackageDetail /></Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/modules"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><Modules /></Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><Subscriptions /></Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><Invoices /></Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><Reports /></Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/admins"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><AdminUsers /></Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Suspense fallback={<PageFallback />}><Settings /></Suspense>
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster theme="dark" richColors position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
